import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Undo2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CopyChatMessage = {
  role?: string;
  text?: string;
  at?: string;
  kind?: string;
  patches?: { slide?: number; text?: string; sub?: string }[];
};

type Props = {
  messages: CopyChatMessage[];
  busy?: boolean;
  canUndo?: boolean;
  disabled?: boolean;
  error?: string;
  onSend: (message: string) => void;
  onReviewRoles: () => void;
  onUndo: () => void;
  reviewPending?: boolean;
  undoPending?: boolean;
};

export default function CopyChatPanel({
  messages,
  busy,
  canUndo,
  disabled,
  error,
  onSend,
  onReviewRoles,
  onUndo,
  reviewPending,
  undoPending,
}: Props) {
  const [draft, setDraft] = useState("");
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, busy]);

  const submit = () => {
    const text = draft.trim();
    if (!text || busy || disabled) return;
    onSend(text);
    setDraft("");
  };

  return (
    <div className="flex min-h-[22rem] flex-col rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div>
          <div className="text-sm font-medium text-slate-900">Czat o treści</div>
          <p className="text-[11px] text-muted-foreground">
            Ustal zmianę w rozmowie. Gdy powiesz „zrób”, redaktorka zmieni wskazane karty.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={disabled || busy || reviewPending}
            onClick={onReviewRoles}
          >
            {reviewPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Users className="mr-1.5 h-3.5 w-3.5" />
            )}
            Review ról
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled || busy || !canUndo || undoPending}
            onClick={onUndo}
          >
            {undoPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Undo2 className="mr-1.5 h-3.5 w-3.5" />
            )}
            Cofnij z czatu
          </Button>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {messages.length === 0 && !busy ? (
          <p className="text-sm text-muted-foreground">
            Np. „slajd 2 za suchy, dodaj ciało” albo „hak ma być o otwieraniu piwa”. Najpierw ustal, potem zatwierdź w rozmowie.
          </p>
        ) : null}
        {messages.map((msg, i) => {
          const mine = (msg.role || "") === "user";
          return (
            <div
              key={`${msg.at || i}-${msg.role || "x"}`}
              className={cn("flex", mine ? "justify-end" : "justify-start")}
            >
              <div
                className={cn(
                  "max-w-[92%] rounded-md px-3 py-2 text-sm whitespace-pre-wrap",
                  mine ? "bg-slate-900 text-white" : "bg-muted/50 text-foreground",
                  msg.kind === "roles_review" && "border border-violet-200 bg-violet-50/80",
                )}
              >
                {!mine && msg.kind === "roles_review" ? (
                  <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-violet-900">
                    Review ról
                  </div>
                ) : null}
                {msg.text}
              </div>
            </div>
          );
        })}
        {busy ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Redaktorka myśli…
          </div>
        ) : null}
        <div ref={bottomRef} />
      </div>

      {error ? <p className="px-3 pb-1 text-xs text-destructive">{error}</p> : null}

      <div className="flex gap-2 border-t border-border p-3">
        <textarea
          className="min-h-[64px] flex-1 resize-y rounded-md border border-input bg-transparent px-2 py-1.5 text-sm"
          placeholder="Napisz, co zmienić na której karcie…"
          value={draft}
          disabled={disabled || busy}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <Button type="button" size="icon" disabled={disabled || busy || !draft.trim()} onClick={submit}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
